import { Embroidery, EmbroideryKey, I18NText, PartialActionDispatch, StateObj } from 'src/types'
import { isHalfWidthCharChecker } from 'src/logic'
import { emojiChecker } from 'src/logic'
import { useEmbroideryContent } from 'src/hooks/useEmbroideryContent'
import { useEmbroideriesDispatcher } from 'src/hooks/useEmbroideriesDispatcher'
import { APL_UR002_PLUS_PRODUCT_NUMBER } from '../../japan/constants/baseModel'
import { CURSIVE, SANS_SERIF, SERIF } from '../../constants/embroidery'

export const checkUsAtomsCharacter = (embroidery: Embroidery, _: string, t: I18NText) => {
  const { content, position } = embroidery
  const isHalfWidthChar = isHalfWidthCharChecker(content)
  const isNotFirstEmoji = !emojiChecker(content[0]) && !['\uDE77', '\uD83E'].includes(content[0])
  const isJapanese = content.charCodeAt(0) >= 256 && isNotFirstEmoji
  const isKoreaContents = content.charCodeAt(0) >= 40001 && !emojiChecker(content[0]) && !['\uDE77', '\uD83E'].includes(content[0])
  const typeFaceObjs = t.EMBROIDERY_TYPEFACE_OBJECTS
  const typeFaceJaObjs = typeFaceObjs.filter((t) => [SERIF.value].includes(t.value))
  const typeFaceEnObjs = typeFaceObjs.filter((t) => [SANS_SERIF.value, CURSIVE.value].includes(t.value))
  const typeFaceObjects = isJapanese ? typeFaceJaObjs : typeFaceEnObjs
  const isThumbPosition = position.value === 'thumbFinger'
  const mustExpandCharSize = isThumbPosition
  const halfWidthChar = mustExpandCharSize ? 20 : 12
  const fullWidthChar = mustExpandCharSize ? 10 : 6

  return {
    contentMaxLength: isHalfWidthChar ? halfWidthChar : fullWidthChar,
    existsContent: content.length > 0,
    typeFaceObjects,
    isKoreaContents,
    contentCaution: isKoreaContents ? '한자,영어만 자수가능합니다. 한글은 자수 불가입니다.' : ''
  }
}

export const getUsAtomsPositions = (productNumber: string, t: I18NText): StateObj[] => {
  const isErmCatcherMitt = productNumber === APL_UR002_PLUS_PRODUCT_NUMBER
  const position1 = isErmCatcherMitt ? t.EMBROIDERY_POSITION_OBJECTS.filter((p) => p.value !== 'thumbFinger') : t.EMBROIDERY_POSITION_OBJECTS

  // USモデルはファーストミットが存在しないため、常に親指部分は選択不可とする。そのため、親指部分を除外したオブジェクトを生成する。
  const position2 = position1.filter((p) => p.value !== 'pinkyFingerSideBack')
  return position2
}

export const useUsAtomsEmbroideriesDispatch = (dispatch: PartialActionDispatch, embroideries: Embroidery[], t: I18NText) => {
  const atomsEmbroideriesDispatcher = (key: EmbroideryKey) => {
    const atomsObjects = {
      position: t.EMBROIDERY_POSITION_OBJECTS,
      typeFace: t.EMBROIDERY_TYPEFACE_OBJECTS,
      color: t.EMBROIDERY_COLOR_OBJECTS,
      shadowColor: t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS,
      edgeColor: t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS
    }
    return (selected: string, i?: number) => useEmbroideriesDispatcher(dispatch, embroideries, selected, atomsObjects, key, i)
  }
  const handleContentAndNum = useEmbroideryContent(dispatch, embroideries)
  return {
    handle: {
      typeFace: atomsEmbroideriesDispatcher('typeFace'),
      position: atomsEmbroideriesDispatcher('position'),
      color: atomsEmbroideriesDispatcher('color'),
      shadowColor: atomsEmbroideriesDispatcher('shadowColor'),
      edgeColor: atomsEmbroideriesDispatcher('edgeColor'),
      content: (selected: string, i?: number) => {
        if (i === undefined) return
        handleContentAndNum(selected, i)
      }
    }
  }
}

export const getUsAtomsCaution = (condition: boolean, key: 'position' | 'typeFace' | 'shadowColor' | 'edgeColor', t: I18NText): string => {
  if (condition) {
    switch (key) {
      case 'position':
        return t.CAUTION_EMBROIDERY_POSITION
      case 'typeFace':
        return t.CAUTION_EMBROIDERY_TYPEFACE
      case 'shadowColor':
      case 'edgeColor':
        return t.CAUTION_EMBROIDERY_SHADOW_EDGE_COLOR
    }
  } else {
    return ''
  }
}

export const getContentCaution = (existEmoji: boolean, contentCaution: string, isUnSelectedPosition: boolean, t: I18NText): string => {
  if (existEmoji) {
    return t.EMBROIDERY_CONTENT_EMOJI_CAUTION
  }
  if (contentCaution.length > 0) {
    return contentCaution
  }
  return isUnSelectedPosition ? t.CAUTION_EMBROIDERY_CONTENTS : ''
}
